/**
 * iSale Brand System v1.0 — locked 2026-05-16 by design lead.
 *
 * Source: document/design/iSale Brand.html (canonical reference).
 * Consumed by:
 *   - apps/isale-landing  (Tailwind theme.extend)
 *   - apps/isale-web      (Tailwind theme.extend)
 *   - apps/isale-mobile   (NativeWind tailwind preset)
 *
 * Update policy: bump version in package.json + add to design-system PR
 * checklist when changing any palette / typography / radius value.
 */

// ───────── Palette ─────────

export const color = {
  indigo: {
    50: "#EEF2FF",
    100: "#E0E7FF",
    200: "#C7D2FE",
    300: "#A5B4FC",
    400: "#818CF8",
    500: "#6366F1", // brand primary
    600: "#4F46E5", // hover / active
    700: "#4338CA",
    900: "#312E81", // dark surface tint
  },
  pink: {
    200: "#FBCFE8",
    400: "#F472B6", // accent
    600: "#DB2777",
  },
  ink: "#0B0C1A",
  success: "#10B981",
  warn: "#F59E0B",
  error: "#EF4444",
  slate: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
} as const;

// ───────── Spacing ─────────

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 48,
} as const;

// ───────── Radii ─────────

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  hero: 24,
  pill: 999,
} as const;

// ───────── Typography ─────────

export const font = {
  family: {
    sans: "'Plus Jakarta Sans', system-ui, sans-serif",
    mono: "'JetBrains Mono', monospace",
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 800,
  },
} as const;

/**
 * ADR-011 micro-type scale — sub-`text-xs` sizes prescribed by the design
 * HTMLs (notification rows, settings labels, badges, dense tables) that have
 * no Tailwind default slot. Spread into `theme.extend.fontSize` on web +
 * mobile so `text-2xs … text-6xs` + `text-cap` replace the arbitrary
 * `text-[Npx]` values (and the DRIFT-BYPASS headers that documented them).
 *   xs = 12px (Tailwind default) · sm = 14px · base = 16px
 *   cap = 13px (compact list-row / caption — between xs and sm)
 *   2xs…6xs = 11/10/9/8/7px (descending micro scale)
 */
export const microFontSize = {
  cap: "13px",
  "2xs": "11px",
  "3xs": "10px",
  "4xs": "9px",
  "5xs": "8px",
  "6xs": "7px",
} as const;

// ───────── Gradients ─────────

export const gradients = {
  /** Primary CTA fill — 180deg indigo-400 → indigo-600. */
  primaryCTA: ["#818CF8", "#4F46E5"] as const,
  /** Hero background — diagonal indigo → pink. */
  hero: ["#4F46E5", "#6366F1", "#F472B6"] as const,
  /** Dark hero — for footers and CTAs on ink surface. */
  darkHero: ["#0B0C1A", "#312E81", "#4F46E5"] as const,
} as const;

// ───────── Shadows ─────────

export const shadows = {
  card: "0 1px 0 rgba(15,23,42,.03), 0 2px 4px -2px rgba(15,23,42,.06)",
  float: "0 12px 24px -12px rgba(15,23,42,.18)",
  // FAB-scale glow — reserved for floating elements + gradient-hero cards
  // (ADR-017). NOT the default button shadow.
  brandFAB: "0 14px 28px -8px rgba(79,70,229,.45)",
  // Primary CTA drop-shadow — Light/Dark Mode v2 `.btn-primary` (ADR-017).
  // Buttons previously wore brandFAB, whose 14/28 glow bleeds over content
  // below on light surfaces; v2 specs this tighter 8/16 scale for buttons.
  brandCta: "0 8px 16px -4px rgba(99,102,241,.4)",
} as const;

// ───────── Motion (P0 · 2026-05-20) ─────────

/**
 * Motion tokens — locked per `document/design/iSale Motion Spec.html`.
 *
 * Each token exposes a duration (ms), a CSS-friendly easing string, and an
 * "rn" form for React Native. RN consumers should use Easing.bezier(...) for
 * the curve and `motion.spring.rn` for the Reanimated spring config.
 *
 *   fast       120ms ease-out                 · press-down, hover, ripple in
 *   standard   220ms ease-out                 · hover, fade, chip toggle
 *   emphasized 320ms cubic-bezier(.32,.72,0,1)· page transition, sheet, drawer
 *   spring     380ms cubic-bezier(.25,.46,.45,1.4) · toast, FAB, overshoot
 *   celebrate  600ms cubic-bezier(.34,1.56,.64,1)  · render-done check, milestone
 *   long       1600ms linear                  · skeleton shimmer, progress ring
 */
export type MotionToken = {
  duration: number;
  /** CSS `transition-timing-function` value. */
  cssEasing: string;
  /** Reanimated / Animated bezier control points [x1, y1, x2, y2]. */
  bezier: readonly [number, number, number, number] | null;
};

export const motion = {
  fast: {
    duration: 120,
    cssEasing: "cubic-bezier(0, 0, 0.2, 1)",
    bezier: [0, 0, 0.2, 1],
  },
  standard: {
    duration: 220,
    cssEasing: "cubic-bezier(0, 0, 0.2, 1)",
    bezier: [0, 0, 0.2, 1],
  },
  emphasized: {
    duration: 320,
    cssEasing: "cubic-bezier(0.32, 0.72, 0, 1)",
    bezier: [0.32, 0.72, 0, 1],
  },
  spring: {
    duration: 380,
    cssEasing: "cubic-bezier(0.25, 0.46, 0.45, 1.4)",
    bezier: [0.25, 0.46, 0.45, 1.4],
    /** Reanimated `withSpring` config — from RN Handoff §02. */
    rn: { damping: 14, stiffness: 170 },
  },
  celebrate: {
    duration: 600,
    cssEasing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    bezier: [0.34, 1.56, 0.64, 1],
  },
  long: {
    duration: 1600,
    cssEasing: "linear",
    bezier: null,
  },
} as const;

// ───────── Notification tokens (P0 · 2026-05-20; dark variants added post-F4) ─────────

/**
 * Notification tokens — extracted from `document/design/iSale Notifications.html`
 * (the `notifTokens.ts` snippet block, lines ~3349-3377).
 *
 * Drives every notification surface across mobile + web: Toast, Inline Alert,
 * Sticky Banner, Quota Card, Badge, Inbox, Snackbar, Status Line, Push.
 *
 * Dark variants chosen to keep WCAG AA contrast against the dark theme
 * `bg.elevated = #334155` (slate-700). Background tints darken to ~25%
 * opacity over slate-900; foreground text lifts to 300-level hex so it
 * stays readable; accent stays brand-locked (used for the dot/strip,
 * not the fill, so its contrast against the dark bg is fine).
 */
export const notifVariantsLight = {
  success: { fg: "#047857", bg: "#ECFDF5", border: "#A7F3D0", accent: "#10B981" },
  warning: { fg: "#78350F", bg: "#FFFBEB", border: "#FDE68A", accent: "#F59E0B" },
  error: { fg: "#881337", bg: "#FFF1F2", border: "#FECDD3", accent: "#F43F5E" },
  info: { fg: "#3730A3", bg: "#EEF2FF", border: "#C7D2FE", accent: "#6366F1" },
} as const;

export const notifVariantsDark = {
  success: { fg: "#6EE7B7", bg: "#064E3B", border: "#065F46", accent: "#34D399" },
  warning: { fg: "#FCD34D", bg: "#451A03", border: "#78350F", accent: "#FBBF24" },
  error: { fg: "#FDA4AF", bg: "#4C0519", border: "#881337", accent: "#FB7185" },
  info: { fg: "#A5B4FC", bg: "#1E1B4B", border: "#312E81", accent: "#818CF8" },
} as const;

/** Resolve the variant palette for the current theme mode. */
export function notifVariant(
  variant: keyof typeof notifVariantsLight,
  mode: ThemeMode = "light",
) {
  return mode === "dark" ? notifVariantsDark[variant] : notifVariantsLight[variant];
}

export const notifTokens = {
  /** Default (light) variants — kept for backward-compat with pre-dark
   *  callers. New callers should use `notifVariant(name, mode)`. */
  variant: notifVariantsLight,
  radius: { toast: 16, alert: 14, modal: 18, banner: 0, badge: 999 },
  shadow: {
    toast: "0 10px 24px -10px rgba(15,23,42,.18)",
    modal: "0 30px 60px -20px rgba(15,23,42,.5)",
    push: "0 14px 30px -16px rgba(15,23,42,.45)",
  },
  /** Durations in ms. `hold: null` means caller controls dismissal. */
  duration: {
    toast: { in: 200, out: 150, hold: 4000 },
    snackbar: { in: 220, out: 180, hold: 5000 },
    push: { in: 280, out: 200, hold: null as number | null },
    banner: { in: 220, out: 180, hold: null as number | null },
  },
  zIndex: {
    banner: 50,
    toast: 70,
    snackbar: 80,
    modalBackdrop: 85,
    modal: 90,
  },
} as const;

export type NotifVariant = keyof typeof notifTokens.variant;

// ───────── Theme-aware semantic tokens (MP9) ─────────

export type ThemeMode = "light" | "dark";

/**
 * Semantic color tokens that flip with theme mode. Consumed by both the
 * mobile NativeWind config and the web Tailwind config via CSS variables
 * set by the ThemeProvider at runtime.
 *
 * Dark values pulled from `iSale Dark Mode v2.html` cheat-sheet (D2).
 * WCAG AA contrast pairs verified per the contrast suite (MP9 §6.3).
 */
export interface SemanticColors {
  bg: {
    primary: string;
    card: string;
    elevated: string;
    overlay: string;
    inverse: string;
  };
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string;
    link: string;
  };
  border: {
    default: string;
    strong: string;
    subtle: string;
  };
  brand: {
    primary: string;
    hover: string;
    soft: string;
  };
  accent: {
    pink: string;
    amber: string;
    red: string;
    green: string;
  };
}

export const themes: Record<ThemeMode, SemanticColors> = {
  light: {
    bg: {
      primary: "#FFFFFF",
      card: "#F8FAFC",
      elevated: "#FFFFFF",
      overlay: "rgba(15, 23, 42, 0.5)",
      inverse: "#0F172A",
    },
    text: {
      primary: "#0F172A",
      secondary: "#475569",
      tertiary: "#94A3B8",
      inverse: "#FFFFFF",
      link: "#4F46E5",
    },
    border: {
      default: "#E2E8F0",
      strong: "#CBD5E1",
      subtle: "#F1F5F9",
    },
    brand: {
      primary: "#4F46E5",
      hover: "#4338CA",
      soft: "#EEF2FF",
    },
    accent: {
      pink: "#F472B6",
      amber: "#F59E0B",
      red: "#EF4444",
      green: "#10B981",
    },
  },
  dark: {
    bg: {
      primary: "#0F172A",
      card: "#1E293B",
      elevated: "#334155",
      overlay: "rgba(0, 0, 0, 0.7)",
      inverse: "#F8FAFC",
    },
    text: {
      primary: "#F8FAFC",
      secondary: "#CBD5E1",
      tertiary: "#94A3B8",
      inverse: "#0F172A",
      link: "#A5B4FC",
    },
    border: {
      default: "#334155",
      strong: "#475569",
      subtle: "#1E293B",
    },
    brand: {
      primary: "#818CF8",
      hover: "#A5B4FC",
      soft: "#312E81",
    },
    accent: {
      pink: "#F9A8D4",
      amber: "#FBBF24",
      red: "#F87171",
      green: "#34D399",
    },
  },
};

/**
 * CSS-variable name map. ThemeProvider on web sets `:root` styles per
 * mode; on mobile NativeWind 4 resolves these via the dark: variant +
 * cssInterop on the root <View>.
 *
 * Token names use kebab-case for direct CSS variable consumption.
 */
export const cssVarNames = {
  bg: {
    primary: "--bg-primary",
    card: "--bg-card",
    elevated: "--bg-elevated",
    overlay: "--bg-overlay",
    inverse: "--bg-inverse",
  },
  text: {
    primary: "--text-primary",
    secondary: "--text-secondary",
    tertiary: "--text-tertiary",
    inverse: "--text-inverse",
    link: "--text-link",
  },
  border: {
    default: "--border-default",
    strong: "--border-strong",
    subtle: "--border-subtle",
  },
  brand: {
    primary: "--brand-primary",
    hover: "--brand-hover",
    soft: "--brand-soft",
  },
  accent: {
    pink: "--accent-pink",
    amber: "--accent-amber",
    red: "--accent-red",
    green: "--accent-green",
  },
} as const;

/** Flatten the semantic palette to CSS variable declarations. */
export function toCssVars(mode: ThemeMode): Record<string, string> {
  const t = themes[mode];
  return {
    [cssVarNames.bg.primary]: t.bg.primary,
    [cssVarNames.bg.card]: t.bg.card,
    [cssVarNames.bg.elevated]: t.bg.elevated,
    [cssVarNames.bg.overlay]: t.bg.overlay,
    [cssVarNames.bg.inverse]: t.bg.inverse,
    [cssVarNames.text.primary]: t.text.primary,
    [cssVarNames.text.secondary]: t.text.secondary,
    [cssVarNames.text.tertiary]: t.text.tertiary,
    [cssVarNames.text.inverse]: t.text.inverse,
    [cssVarNames.text.link]: t.text.link,
    [cssVarNames.border.default]: t.border.default,
    [cssVarNames.border.strong]: t.border.strong,
    [cssVarNames.border.subtle]: t.border.subtle,
    [cssVarNames.brand.primary]: t.brand.primary,
    [cssVarNames.brand.hover]: t.brand.hover,
    [cssVarNames.brand.soft]: t.brand.soft,
    [cssVarNames.accent.pink]: t.accent.pink,
    [cssVarNames.accent.amber]: t.accent.amber,
    [cssVarNames.accent.red]: t.accent.red,
    [cssVarNames.accent.green]: t.accent.green,
  };
}

// ───────── ADR-011 unified semantic layer (P1) ─────────

/**
 * ADR-011 — the single, theme-aware semantic colour layer. Derived ENTIRELY
 * from `color` above (no new hues): apps consume these only through
 * token-mapped Tailwind / NativeWind classes (`bg-primary`, `text-fg`,
 * `border-default`, `bg-surface`, …), never raw hex and never `useThemeColors`.
 *
 * Mechanism: each token is emitted as a CSS variable (`--color-*`). Light
 * values live on `:root`, dark values on `.dark` — flipping the whole UI by
 * toggling one root class (web ThemeProvider already does; mobile via the
 * NativeWind dark class). See `cssVariablesStylesheet()` + `semanticTailwindColors`.
 */
export interface UnifiedSemantic {
  /** Page background. */
  bg: string;
  /** Card / raised surface. */
  surface: string;
  /** Default hairline border. */
  border: string;
  /** Primary foreground / body text. */
  fg: string;
  /** Secondary text. */
  fgMuted: string;
  /** Tertiary / placeholder text. */
  fgSubtle: string;
  /** Raised surface above `surface` (modals, popovers). */
  elevated: string;
  /** Scrim behind modals / drawers (rgba). */
  overlay: string;
  /** Inverse background (e.g. dark chip on light bg). */
  bgInverse: string;
  /** Text on inverse / brand-filled surfaces. */
  fgInverse: string;
  /** Hyperlink text. */
  link: string;
  /** Stronger hairline (dividers needing emphasis). */
  borderStrong: string;
  /** Subtle hairline (inset rows). */
  borderSubtle: string;
  /** Brand primary (default). */
  primary: string;
  /** Brand primary hover / active. */
  primaryHover: string;
  /** Brand primary pressed / strongest. */
  primaryStrong: string;
  /** Brand primary tint / soft fill (chips, secondary hover). */
  primarySoft: string;
  /** Filled primary-button surface — saturated indigo-600, IDENTICAL in light +
   *  dark, so white text passes WCAG 1.4.3 (≥4.5:1) in BOTH modes (ADR-013).
   *  Use for filled CTAs with `text-white`; the adaptive `primary*` tokens stay
   *  for text/border/accent (which must lighten in dark). */
  primaryFill: string;
  /** Pressed/active state of `primaryFill` — indigo-700 (both modes). */
  primaryFillPressed: string;
  /** Brand accent (pink) — brand-locked, same in both modes. */
  accent: string;
  /** Accent soft fill (tint bg for accent chips/selected states). */
  accentSoft: string;
  /** Accent strong (darker pink — icon/border/text on accentSoft). */
  accentStrong: string;
  /** Readable text/icon on `accentSoft` (WCAG AA). */
  accentFg: string;
  success: string;
  /** Success soft fill (tint bg for chips/alerts). */
  successSoft: string;
  /** Readable text/icon on `successSoft` (WCAG AA). */
  successFg: string;
  warn: string;
  /** Warning soft fill. */
  warnSoft: string;
  /** Readable text/icon on `warnSoft` (WCAG AA). */
  warnFg: string;
  danger: string;
  /** Danger soft fill (danger button bg, alert tint). */
  dangerSoft: string;
  /** Readable text/icon on `dangerSoft` (WCAG AA). */
  dangerFg: string;
}

export const semantic: Record<ThemeMode, UnifiedSemantic> = {
  light: {
    bg: color.slate[50], // ADR-015: page = slate-50 (v2 --l-bg); grey-canvas + white-cards
    surface: "#FFFFFF", // ADR-015: card/sheet = white (v2 --l-surface); elevation via shadow
    elevated: "#FFFFFF",
    overlay: "rgba(15, 23, 42, 0.5)",
    bgInverse: color.slate[900],
    border: color.slate[200],
    borderStrong: color.slate[300],
    borderSubtle: color.slate[100],
    fg: color.slate[900],
    fgMuted: color.slate[600],
    fgSubtle: color.slate[500], // ADR-014: slate-400→500 for AA on white (4.76:1; was 2.56:1)
    fgInverse: "#FFFFFF",
    link: color.indigo[600],
    primary: color.indigo[500],
    primaryHover: color.indigo[600],
    primaryStrong: color.indigo[700],
    primarySoft: color.indigo[50],
    primaryFill: color.indigo[600],
    primaryFillPressed: color.indigo[700],
    accent: color.pink[400],
    accentSoft: "#FCE7F3",
    accentStrong: color.pink[600],
    accentFg: color.pink[600],
    success: color.success,
    successSoft: notifVariantsLight.success.bg,
    successFg: notifVariantsLight.success.fg,
    warn: color.warn,
    warnSoft: notifVariantsLight.warning.bg,
    warnFg: notifVariantsLight.warning.fg,
    danger: color.error,
    dangerSoft: notifVariantsLight.error.bg,
    dangerFg: notifVariantsLight.error.fg,
  },
  dark: {
    bg: color.slate[900],
    surface: color.slate[800],
    elevated: color.slate[700],
    overlay: "rgba(0, 0, 0, 0.7)",
    bgInverse: color.slate[50],
    border: color.slate[700],
    borderStrong: color.slate[600],
    borderSubtle: color.slate[800],
    fg: color.slate[50], // ADR-015: match v2 --d-text (slate-50; was slate-100)
    fgMuted: color.slate[300], // ADR-015: body text = slate-300 (v2 --d-text-2); re-split from fgSubtle
    fgSubtle: color.slate[400], // ADR-014: slate-500→400 for AA on dark surface (5.71:1; was 3.07:1)
    fgInverse: color.slate[900],
    link: color.indigo[300],
    primary: color.indigo[400],
    primaryHover: color.indigo[300],
    primaryStrong: color.indigo[500],
    primarySoft: color.indigo[900],
    primaryFill: color.indigo[600],
    primaryFillPressed: color.indigo[700],
    accent: color.pink[400],
    accentSoft: "#500724",
    accentStrong: color.pink[400],
    accentFg: color.pink[200],
    success: color.success,
    successSoft: notifVariantsDark.success.bg,
    successFg: notifVariantsDark.success.fg,
    warn: color.warn,
    warnSoft: notifVariantsDark.warning.bg,
    warnFg: notifVariantsDark.warning.fg,
    danger: color.error,
    dangerSoft: notifVariantsDark.error.bg,
    dangerFg: notifVariantsDark.error.fg,
  },
};

/** Semantic token → CSS variable name (kebab, `--color-*` namespace). */
export const semanticVarNames: Record<keyof UnifiedSemantic, string> = {
  bg: "--color-bg",
  surface: "--color-surface",
  elevated: "--color-elevated",
  overlay: "--color-overlay",
  bgInverse: "--color-bg-inverse",
  border: "--color-border",
  borderStrong: "--color-border-strong",
  borderSubtle: "--color-border-subtle",
  fg: "--color-fg",
  fgMuted: "--color-fg-muted",
  fgSubtle: "--color-fg-subtle",
  fgInverse: "--color-fg-inverse",
  link: "--color-link",
  primary: "--color-primary",
  primaryHover: "--color-primary-hover",
  primaryStrong: "--color-primary-strong",
  primarySoft: "--color-primary-soft",
  primaryFill: "--color-primary-fill",
  primaryFillPressed: "--color-primary-fill-pressed",
  accent: "--color-accent",
  accentSoft: "--color-accent-soft",
  accentStrong: "--color-accent-strong",
  accentFg: "--color-accent-fg",
  success: "--color-success",
  successSoft: "--color-success-soft",
  successFg: "--color-success-fg",
  warn: "--color-warn",
  warnSoft: "--color-warn-soft",
  warnFg: "--color-warn-fg",
  danger: "--color-danger",
  dangerSoft: "--color-danger-soft",
  dangerFg: "--color-danger-fg",
};

function semanticVarBlock(mode: ThemeMode): string {
  const s = semantic[mode];
  return (Object.keys(semanticVarNames) as Array<keyof UnifiedSemantic>)
    .map((k) => `  ${semanticVarNames[k]}: ${s[k]};`)
    .join("\n");
}

/**
 * ADR-011 — the `:root{…}` (light) + `.dark{…}` (dark) stylesheet for the
 * unified semantic tokens. Inject once into a global `<style>` (web) or
 * global.css (mobile); the `.dark` root class flips every token with no JS.
 */
export function cssVariablesStylesheet(): string {
  return `:root {\n${semanticVarBlock("light")}\n}\n\n.dark {\n${semanticVarBlock("dark")}\n}\n`;
}

/**
 * ADR-011 — CSS variable values for NativeWind mobile (vars() injection).
 *
 * Returns a `Record<"--color-*", hexString>` suitable for passing to
 * `vars()` from `nativewind` / `react-native-css-interop`. Apply to
 * the root View in ThemeProvider whenever the resolved theme changes.
 *
 * Usage:
 *   import { vars } from 'nativewind';
 *   import { mobileVars } from '@isale/design-tokens';
 *   <View style={vars(mobileVars(resolved))}>…</View>
 */
export function mobileVars(mode: ThemeMode): Record<string, string> {
  const s = semantic[mode];
  return Object.fromEntries(
    (Object.keys(semanticVarNames) as Array<keyof UnifiedSemantic>).map((k) => [
      semanticVarNames[k],
      s[k] as string,
    ]),
  );
}

/**
 * `theme.colors` fragment for the web Tailwind + mobile NativeWind configs —
 * maps friendly class names to the ADR-011 CSS variables. Spread into
 * `theme.extend.colors`. (Border maps via `semanticBorderColor`.)
 */
export const semanticTailwindColors = {
  primary: {
    DEFAULT: `var(${semanticVarNames.primary})`,
    hover: `var(${semanticVarNames.primaryHover})`,
    strong: `var(${semanticVarNames.primaryStrong})`,
    soft: `var(${semanticVarNames.primarySoft})`,
    // ADR-013 — filled-CTA surface (indigo-600/700, both modes) so white text
    // ≥4.5:1 in dark too. bg-primary-fill / bg-primary-fill-pressed.
    fill: `var(${semanticVarNames.primaryFill})`,
    "fill-pressed": `var(${semanticVarNames.primaryFillPressed})`,
  },
  accent: {
    DEFAULT: `var(${semanticVarNames.accent})`,
    soft: `var(${semanticVarNames.accentSoft})`,
    strong: `var(${semanticVarNames.accentStrong})`,
    fg: `var(${semanticVarNames.accentFg})`,
  },
  link: `var(${semanticVarNames.link})`,
  fg: {
    DEFAULT: `var(${semanticVarNames.fg})`,
    muted: `var(${semanticVarNames.fgMuted})`,
    subtle: `var(${semanticVarNames.fgSubtle})`,
    inverse: `var(${semanticVarNames.fgInverse})`,
  },
  surface: `var(${semanticVarNames.surface})`,
  canvas: `var(${semanticVarNames.bg})`,
  // Border color also exposed as a fill (dividers, drag handles, rails).
  border: `var(${semanticVarNames.border})`,
  elevated: `var(${semanticVarNames.elevated})`,
  overlay: `var(${semanticVarNames.overlay})`,
  inverse: `var(${semanticVarNames.bgInverse})`,
  success: {
    DEFAULT: `var(${semanticVarNames.success})`,
    soft: `var(${semanticVarNames.successSoft})`,
    fg: `var(${semanticVarNames.successFg})`,
  },
  warn: {
    DEFAULT: `var(${semanticVarNames.warn})`,
    soft: `var(${semanticVarNames.warnSoft})`,
    fg: `var(${semanticVarNames.warnFg})`,
  },
  danger: {
    DEFAULT: `var(${semanticVarNames.danger})`,
    soft: `var(${semanticVarNames.dangerSoft})`,
    fg: `var(${semanticVarNames.dangerFg})`,
  },
} as const;

/**
 * `theme.borderColor` fragment → `border` (bare) + `border-default` use the
 * token; `border-strong` / `border-subtle` for the other two weights.
 */
export const semanticBorderColor = {
  DEFAULT: `var(${semanticVarNames.border})`,
  default: `var(${semanticVarNames.border})`,
  strong: `var(${semanticVarNames.borderStrong})`,
  subtle: `var(${semanticVarNames.borderSubtle})`,
} as const;

// ───────── Aggregate ─────────

export const tokens = {
  color,
  spacing,
  radius,
  font,
  gradients,
  shadows,
  motion,
  notifTokens,
  themes,
  cssVarNames,
} as const;

export type ColorPalette = typeof color;
export type Gradients = typeof gradients;
export type Motion = typeof motion;
export type NotifTokens = typeof notifTokens;
